import React from "react";
import { Box, Typography, Grid } from "@mui/material";
import { DirectionsBus } from "@mui/icons-material";
import { formatVehicleNumber } from "../utils/utils";

export interface VehicleProps {
  id?: string;
  status?: string;
  vehicleType?: string;
  acquisitionType?: string;
  vehicleNumber?: string;
  chassisNumber?: string;
  makeYear?: number;
  model?: string;
  color?: string;
  bodyType?: string;
  configuration?: string;
  fuelType?: string;
  seatingCapacity?: number;
  isAC?: boolean;
  nextMaintenanceDate?: string;
  rcNumber?: string;
  rcExpiryDate?: string;
  pucNumber?: string;
  pucEndDate?: string;
  permits?: [
    {
      id?: string;
      vehicleId?: string;
      permitType?: string;
      stateName?: string;
      issuingAuthority?: string;
      permitStartDate?: string;
      permitEndDate?: string;
      isActive?: boolean;
      createdAt?: string;
      updatedAt?: string;
      deletedAt?: string | null;
    }
  ];
  outsourcedOwner?: {
    companyName: string;
    primaryContactName: string;
    primaryContactNumber: string;
    gstPercentage: number;
  } | null;
}

interface BasicVehicleDetailsViewProps {
  vehicle: VehicleProps;
  title?: string;
  small?: boolean;
}

const BasicVehicleDetailsView: React.FC<BasicVehicleDetailsViewProps> = ({
  vehicle,
  title = "Vehicle Details",
  small = false,
}) => {
  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 1,
        boxShadow: 1,
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <DirectionsBus sx={{ fontSize: small ? 25 : 32, color: "#000" }} />
        <Typography
          variant="h6"
          sx={{ fontSize: small ? "1rem" : "1.25rem", fontWeight: 600 }}
        >
          {title}
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ fontSize: small ? "0.75rem" : "0.875rem" }}
          >
            Vehicle Number
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: small ? "0.875rem" : "1rem" }}
          >
            {formatVehicleNumber(vehicle.vehicleNumber || "") ||
              "Not Available"}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ fontSize: small ? "0.75rem" : "0.875rem" }}
          >
            Vehicle Type
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: small ? "0.875rem" : "1rem" }}
          >
            {vehicle?.vehicleType || vehicle?.bodyType
              ? `${vehicle?.vehicleType || ""} ${
                  vehicle?.bodyType || ""
                }`.trim()
              : "Not Available"}
          </Typography>
        </Grid>
        {(vehicle?.model || Boolean(vehicle?.makeYear)) && (
          <Grid item xs={12} sm={6} md={3}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ fontSize: small ? "0.75rem" : "0.875rem" }}
            >
              Make (Year)
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: small ? "0.875rem" : "1rem" }}
            >
              {`${vehicle?.model || "NA"} ${
                vehicle?.makeYear ? `(${vehicle?.makeYear})` : "(NA)"
              }`}
            </Typography>
          </Grid>
        )}
        {(vehicle?.configuration || Boolean(vehicle?.seatingCapacity)) && (
          <Grid item xs={12} sm={6} md={3}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ fontSize: small ? "0.75rem" : "0.875rem" }}
            >
              Configuration (Seats)
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: small ? "0.875rem" : "1rem" }}
            >
              {`${vehicle?.configuration || "NA"} ${
                vehicle?.seatingCapacity
                  ? `(${vehicle?.seatingCapacity} Seats)`
                  : "(NA)"
              }`}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ fontSize: small ? "0.75rem" : "0.875rem" }}
          >
            Fuel Type
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: small ? "0.875rem" : "1rem" }}
          >
            {vehicle?.fuelType || "Not Available"}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ fontSize: small ? "0.75rem" : "0.875rem" }}
          >
            AC
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: small ? "0.875rem" : "1rem" }}
          >
            {vehicle?.isAC !== undefined
              ? vehicle?.isAC
                ? "Yes"
                : "No"
              : "Not Available"}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ fontSize: small ? "0.75rem" : "0.875rem" }}
          >
            Status
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: small ? "0.875rem" : "1rem" }}
          >
            {vehicle?.status || "Not Available"}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ fontSize: small ? "0.75rem" : "0.875rem" }}
          >
            Acquisition Type
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: small ? "0.875rem" : "1rem" }}
          >
            {vehicle?.acquisitionType || "Not Available"}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BasicVehicleDetailsView;
